// The send queue. A revision is frozen as bytes when the rep presses "Send" and is retried
// with the same bytes and the same revision id until the server answers, which makes every
// retry safe (the server stores exactly one revision per id). Photos follow once the
// revision is accepted: PUT to the presigned URL with exactly the signed headers, then
// "complete". Nothing here needs the rep to be online when they press Send.

import { useSyncExternalStore } from "react";

import { authFetch, currentSession } from "../lib/auth";
import { db } from "../lib/db";
import { apiUrl } from "../lib/env";
import { nowIso } from "../lib/time";
import type { Project, RevisionRecord } from "../project/model";
import { getProject, photoBlob, updateProject } from "../project/store";
import { buildSurvey, contentKey, encodeSurvey, sha256Hex } from "../survey/build";
import { errorsOf, reviewProject } from "../survey/checks";
import type { SurveyIssue } from "../survey/types";

export interface OutboxEntry {
  revision_id: string;
  survey_id: string;
  /** Sent only with this user's session in this organisation: another sign-in never sends it. */
  org_id: string;
  user_id: string;
  created_at: string;
  bytes: ArrayBuffer;
  sha256: string;
  state: "queued" | "photos" | "done" | "rejected";
  attempts: number;
  next_attempt_at: number;
  last_error: string | null;
  /** photos of this revision still to upload (sha256) */
  photos: string[];
}

export type QueueResult =
  | { kind: "queued"; revision_id: string }
  | { kind: "unchanged"; revision_id: string }
  | { kind: "not_ready"; messages: string[] };

/** Freeze the project's current state as a new revision and queue it. */
export async function queueRevision(projectId: string, device?: { model?: string; os?: string }): Promise<QueueResult> {
  const project = getProject(projectId);
  if (!project) throw new Error("project not found");
  const errors = errorsOf(reviewProject(project));
  if (errors.length) return { kind: "not_ready", messages: errors.map((e) => e.message) };
  const parent = latestLive(project);
  const revision_id = crypto.randomUUID();
  const doc = buildSurvey(project, {
    revision_id,
    parent_revision_id: parent?.revision_id ?? null,
    captured_at: nowIso(),
    ...(device ? { device } : {}),
  });
  const key = await contentKey(doc);
  if (parent && parent.content_key === key) return { kind: "unchanged", revision_id: parent.revision_id };
  const bytes = encodeSurvey(doc);
  const sha256 = await sha256Hex(bytes);
  const entry: OutboxEntry = {
    revision_id,
    survey_id: project.id,
    org_id: project.org_id,
    user_id: project.user_id,
    created_at: new Date().toISOString(),
    bytes: bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
    sha256,
    state: "queued",
    attempts: 0,
    next_attempt_at: 0,
    last_error: null,
    photos: [],
  };
  await db.put("outbox", entry);
  const record: RevisionRecord = {
    revision_id,
    parent_revision_id: parent?.revision_id ?? null,
    created_at: entry.created_at,
    sha256,
    size_bytes: bytes.byteLength,
    state: "queued",
    content_key: key,
  };
  await updateProject(project.id, (p) => p.revisions.push(record), { edit: false });
  void kick();
  return { kind: "queued", revision_id };
}

/** The newest revision that is (or will be) on the server: the parent of the next one. */
export function latestLive(project: Project): RevisionRecord | undefined {
  return [...project.revisions].reverse().find((r) => r.state !== "rejected");
}

// ------------------------------------------------------------------ sender

const listeners = new Set<() => void>();
let status: { sending: boolean; pending: number; online: boolean; last_error: string | null } = {
  sending: false,
  pending: 0,
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  last_error: null,
};

function setStatus(patch: Partial<typeof status>) {
  status = { ...status, ...patch };
  for (const l of listeners) l();
}

export function useOutboxStatus() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => status,
  );
}

let running: Promise<void> | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

export function kick(): Promise<void> {
  running ??= run().finally(() => {
    running = null;
  });
  return running;
}

export function startOutbox(): () => void {
  const online = () => {
    setStatus({ online: true });
    void kick();
  };
  const offline = () => setStatus({ online: false });
  window.addEventListener("online", online);
  window.addEventListener("offline", offline);
  void kick();
  return () => {
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
    if (timer) clearTimeout(timer);
  };
}

function backoff(attempts: number): number {
  return Math.min(5 * 60_000, 2_000 * 2 ** Math.min(attempts, 8)) * (0.8 + Math.random() * 0.4);
}

async function run(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  const session = currentSession();
  if (!session) return;
  const mine = (e: OutboxEntry) => e.org_id === session.active_org_id && e.user_id === session.user.id;
  setStatus({ sending: true });
  try {
    const entries = (await db.all<OutboxEntry>("outbox")).filter(mine).sort((a, b) => a.created_at.localeCompare(b.created_at));
    // Revisions of one survey go in order (a parent is stored before its child); a survey
    // that is waiting does not hold up the others.
    const blocked = new Set<string>();
    for (const entry of entries) {
      if (entry.state === "done" || entry.state === "rejected") continue;
      if (blocked.has(entry.survey_id)) continue;
      if (entry.next_attempt_at > Date.now() || !(await process(entry))) blocked.add(entry.survey_id);
    }
  } finally {
    const left = (await db.all<OutboxEntry>("outbox")).filter((e) => mine(e) && (e.state === "queued" || e.state === "photos"));
    setStatus({ sending: false, pending: left.length });
    if (left.length) {
      const soonest = Math.max(1_000, Math.min(...left.map((e) => e.next_attempt_at)) - Date.now());
      timer = setTimeout(() => void kick(), Math.min(soonest, 60_000));
    }
  }
}

async function failLater(entry: OutboxEntry, message: string): Promise<false> {
  entry.attempts += 1;
  entry.next_attempt_at = Date.now() + backoff(entry.attempts);
  entry.last_error = message;
  await db.put("outbox", entry);
  setStatus({ last_error: message });
  return false;
}

async function setRecord(entry: OutboxEntry, patch: Partial<RevisionRecord>) {
  if (!getProject(entry.survey_id)) return;
  await updateProject(
    entry.survey_id,
    (p) => {
      const r = p.revisions.find((x) => x.revision_id === entry.revision_id);
      if (r) Object.assign(r, patch);
    },
    { edit: false },
  );
}

interface UploadAnswer {
  revision_no: number;
  warnings: SurveyIssue[];
  photos_pending: number;
  photos: { sha256: string; status: "pending" | "uploaded"; upload: { method: string; url: string; headers: Record<string, string> } | null }[];
}

/** Returns true when the next entry may be processed. */
async function process(entry: OutboxEntry): Promise<boolean> {
  if (entry.state === "queued") {
    let res: Response;
    try {
      res = await authFetch(
        new Request(apiUrl(`/v1/factory/surveys/${entry.survey_id}/revisions/${entry.revision_id}`), {
          method: "PUT",
          headers: { "content-type": "application/json", "x-content-sha256": entry.sha256 },
          body: entry.bytes,
        }),
      );
    } catch {
      return failLater(entry, "Сүлжээ алга");
    }
    const body = (await res.json().catch(() => null)) as (UploadAnswer & { error?: { code: string; message: string; details?: unknown } }) | null;
    if (res.status === 200 || res.status === 201) {
      const answer = body as UploadAnswer;
      entry.state = "photos";
      entry.photos = answer.photos.filter((p) => p.status === "pending").map((p) => p.sha256);
      entry.attempts = 0;
      entry.next_attempt_at = 0;
      entry.last_error = null;
      await db.put("outbox", entry);
      await setRecord(entry, {
        state: "sent",
        revision_no: answer.revision_no,
        warnings: answer.warnings.map((w) => ({ code: w.code, message_mn: w.message_mn })),
        photos_pending: answer.photos_pending,
      });
      await uploadPhotos(entry, answer.photos);
      return true;
    }
    const code = body?.error?.code ?? `http_${res.status}`;
    if (res.status === 422 || res.status === 409 || res.status === 413 || res.status === 403 || res.status === 404) {
      // Retrying the same bytes cannot help: show why and let the rep fix and send again.
      const details = body?.error?.details;
      const issues = Array.isArray(details) ? (details as SurveyIssue[]) : [];
      entry.state = "rejected";
      entry.last_error = code;
      await db.put("outbox", entry);
      await setRecord(entry, {
        state: "rejected",
        errors: issues.length
          ? issues.filter((i) => i.severity !== "warning").map((i) => ({ code: i.code, path: i.path, message_mn: i.message_mn }))
          : [{ code, path: "", message_mn: body?.error?.message ?? code }],
      });
      return true;
    }
    if (res.status === 401) {
      return failLater(entry, "Дахин нэвтэрнэ үү");
    }
    // 400 digest_mismatch (cut on the way), 5xx, 429: the same bytes again, later.
    return failLater(entry, code);
  }
  if (entry.state === "photos") {
    await uploadPhotos(entry, null);
    return true;
  }
  return true;
}

async function uploadPhotos(entry: OutboxEntry, fromAnswer: UploadAnswer["photos"] | null): Promise<void> {
  const remaining: string[] = [];
  for (const sha of entry.photos) {
    let target = fromAnswer?.find((p) => p.sha256 === sha);
    try {
      if (!target || !target.upload) {
        const res = await authFetch(new Request(apiUrl(`/v1/factory/surveys/${entry.survey_id}/photos/${sha}`)));
        if (!res.ok) {
          remaining.push(sha);
          continue;
        }
        target = (await res.json()) as UploadAnswer["photos"][number];
      }
      if (target.status === "uploaded") continue;
      if (!target.upload) {
        remaining.push(sha); // storage not configured on the server yet: try again later
        continue;
      }
      const blob = await photoBlob({ sha256: sha });
      if (!blob) continue; // the file is gone from this device; the server keeps it pending
      const put = await fetch(target.upload.url, { method: target.upload.method, headers: target.upload.headers, body: blob });
      if (!put.ok) {
        remaining.push(sha);
        continue;
      }
      const done = await authFetch(
        new Request(apiUrl(`/v1/factory/surveys/${entry.survey_id}/photos/${sha}/complete`), { method: "POST" }),
      );
      if (!done.ok) remaining.push(sha);
    } catch {
      remaining.push(sha);
    }
  }
  entry.photos = remaining;
  if (remaining.length) {
    entry.attempts += 1;
    entry.next_attempt_at = Date.now() + backoff(entry.attempts);
  } else {
    entry.state = "done";
  }
  await db.put("outbox", entry);
  await setRecord(entry, { photos_pending: remaining.length });
}
